import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_REQUIRED_DOCUMENTS_QUERY } from '../queries';
import { REQUIRED_DOCUMENT_DELETED } from '../subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_REQUIRED_DOCUMENTS } from '../mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import RequiredDocumentItem from './RequiredDocumentItem';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import useSubscription from 'hooks/apollo/useSubscription';

export const requiredDocumentsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'documentName',
      caption: 'RequiredDocument.DocumentName',
      type: 'text'
    },
    {
      name: 'documentCode',
      caption: 'RequiredDocument.DocumentCode',
      type: 'text'
    },
    {
      name: 'sharePointContentType',
      caption: 'RequiredDocument.SharePointContentType',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'RequiredDocument.DocumentName',
      value: 'DocumentName'
    },
    {
      caption: 'RequiredDocument.DocumentCode',
      value: 'DocumentCode'
    },
    {
      caption: 'RequiredDocument.SharePointContentType',
      value: 'SharePointContentType'
    }
  ],
  pager: {
    orderBy: 'DocumentName',
    direction: 'asc'
  }
});

const RequiredDocumentListBrowser = () => {
  const [t] = useTranslation();
  const confirm = useConfirmDialog();

  const addToast = useToast();
  const client = useApolloClient();
  const [activeService] = useContext(ServiceContext);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_REQUIRED_DOCUMENTS_QUERY,
        name: 'pagedRequiredDocuments'
      }
    }),
    []
  );

  useSubscription(REQUIRED_DOCUMENT_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('RequiredDocument.RequiredDocumentRemoved'), 'success');
      client.cache.evict({ id: `RequiredDocumentItem:{"requiredDocTypeId":${data.requiredDocumentDeleted.requiredDocumentId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedRequiredDocuments' });
      client.cache.gc();
    }
  });

  const [deleteRequiredDocument] = useMutation(DELETE_REQUIRED_DOCUMENTS);

  const handleDelete = useCallback(
    requiredDocumentId => {
      confirm(
        () => {
          deleteRequiredDocument({ variables: { input: { requiredDocumentId } } });
        },
        {
          content: t('RequiredDocument.DeleteRequiredDocument')
        }
      );
    },
    [deleteRequiredDocument, confirm, t]
  );

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedRequiredDocuments' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'targetAreas' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'requiredDocTypes' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericEntityFlowsDictionary' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericEntityBusinessStatesXFlow' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'requiredDocument' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'requiredDocumentProperties' });
      client.cache.gc();
    }
  }, [activeService, client]);

  const itemComponent = useCallback(({ data }) => <RequiredDocumentItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'documentName', caption: 'RequiredDocument.DocumentName' },
      { name: 'documentCode', caption: 'RequiredDocument.DocumentCode' },
      { name: 'sharePointContentType', caption: 'RequiredDocument.SharePointContentType' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='required-documets-browser' store={requiredDocumentsStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={requiredDocumentsStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={requiredDocumentsStore} options={fetchingOptions} />
    </Card>
  );
};

export default RequiredDocumentListBrowser;
